"use client";

import { Box } from "@mui/material";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { getImageDimensionsByImageUrl } from "../../../../DesignEditor/views/DesignEditor/utils/smartImageUtils";
import { useBrushOnCanvas } from "../../../../hooks/brush/useBrushOnCanvas";
import { useAppStore } from "../../../../hooks/useStores";
import type { AutomotiveImageSegmentation, AutomotiveReflectionLayer } from "../../../../models/playground";
import styles from "./AutomotiveCanvas.module.scss";

type AutomotiveCanvasProps = {
	baseImageUrl: string;
	reflectionLayers: AutomotiveReflectionLayer[] | undefined;
	canvasRef: (ref: HTMLCanvasElement | null) => void;
	canvasOverlayRef: (ref: HTMLCanvasElement | null) => void;
};

const AutomotiveCanvas = ({ baseImageUrl, reflectionLayers, canvasRef, canvasOverlayRef }: AutomotiveCanvasProps) => {
	const layerCanvasRefs = useRef<Map<string, HTMLCanvasElement>>(new Map());
	const [width, setWidth] = useState<number>();
	const [height, setHeight] = useState<number>();
	const { playgroundStore, imageToImageStore } = useAppStore();
	const { editingCanvasStore } = imageToImageStore;
	const { automotiveStore } = playgroundStore;
	const { reflectionStore } = automotiveStore;

	const containerRef = useRef<null | HTMLDivElement>(null);
	const loadingContainerRef = useRef<HTMLDivElement | null>(null);
	const localCanvasRef = useRef<HTMLCanvasElement | null>(null);
	const localCanvasOverlayRef = useRef<null | HTMLCanvasElement>(null);
	const cursorRef = useRef<HTMLDivElement | null>(null);

	const selectedSegmentation = playgroundStore.selectedSegmentation;
	const brushConfigs = editingCanvasStore.brushConfigs;

	const { clearBrushOverlayCanvasRef } = useBrushOnCanvas(
		brushConfigs,
		localCanvasRef,
		localCanvasOverlayRef,
		cursorRef,
	);

	const [magnifierEnabled, setMagnifierEnabled] = useState<boolean>(false);
	const [magnifierPosition, setMagnifierPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
	const [magnifierSize, setMagnifierSize] = useState<number>(150);

	useEffect(() => {
		getImageDimensions();
		clearBrushOverlayCanvasRef();
	}, [baseImageUrl]);

	useEffect(() => {
		if (playgroundStore.isEditingReflection && magnifierEnabled) {
			setMagnifierEnabled(false);
			reflectionStore.setMagnifierEnabled(false);
		}
	}, [playgroundStore.isEditingReflection]);

	useEffect(() => {
		if (canvasRef && canvasOverlayRef && localCanvasRef.current && localCanvasOverlayRef.current) {
			canvasRef(localCanvasRef.current);
			canvasOverlayRef(localCanvasOverlayRef.current);
		}
		const canvas = localCanvasRef.current;
		const context = canvas?.getContext("2d");
		const container = containerRef.current;

		if (!canvas || !context || !container) {
			return;
		}

		const updateCanvasSize = () => {
			const img = new Image();
			img.crossOrigin = "anonymous";
			img.src = baseImageUrl;

			img.onload = () => {
				const { width: containerWidth, height: containerHeight } = container.getBoundingClientRect();

				const imgAspectRatio = img.width / img.height;
				let drawWidth = img.width;
				let drawHeight = img.height;

				if (drawWidth > containerWidth) {
					drawWidth = containerWidth;
					drawHeight = drawWidth / imgAspectRatio;
				}

				if (drawHeight > containerHeight) {
					drawHeight = containerHeight;
					drawWidth = drawHeight * imgAspectRatio;
				}

				canvas.width = drawWidth;
				canvas.height = drawHeight;
				if (localCanvasOverlayRef && localCanvasOverlayRef.current) {
					localCanvasOverlayRef.current.width = drawWidth;
					localCanvasOverlayRef.current.height = drawHeight;
				}

				if (loadingContainerRef && loadingContainerRef.current) {
					loadingContainerRef.current.style.width = `${drawWidth}px`;
					loadingContainerRef.current.style.height = `${drawHeight}px`;
				}
				context.clearRect(0, 0, canvas.width, canvas.height);
				context.drawImage(img, 0, 0, drawWidth, drawHeight);
			};
		};
		clearBrushOverlayCanvasRef();

		updateCanvasSize();
		window.addEventListener("resize", updateCanvasSize);

		return () => {
			window.removeEventListener("resize", updateCanvasSize);
			if (canvasRef) {
				canvasRef(null);
			}
		};
	}, [baseImageUrl, selectedSegmentation]);

	const getImageDimensions = async () => {
		const dimensions = await getImageDimensionsByImageUrl(baseImageUrl);
		if (dimensions) {
			setWidth(dimensions.width);
			setHeight(dimensions.height);
		}
	};

	const drawnLayersRef = useRef<Set<string>>(new Set());

	useEffect(() => {
		if (!reflectionLayers || !width || !height) return;

		reflectionLayers.forEach((layer) => {
			const canvas = layerCanvasRefs.current.get(layer.name);
			if (!canvas) return;

			const isVisible = !layer.hidden;
			const isDrawn = drawnLayersRef.current.has(layer.name);

			if (isVisible && !isDrawn) {
				drawSingleLayer(layer);
				drawnLayersRef.current.add(layer.name);
			} else if (!isVisible && isDrawn) {
				drawnLayersRef.current.delete(layer.name);
			} else if (isVisible && isDrawn) {
				canvas.style.opacity = `${layer.intensity}`;
			}
		});
	}, [reflectionLayers, width, height]);

	useEffect(() => {
		setMagnifierEnabled(reflectionStore.magnifierEnabled);
	}, [reflectionStore.magnifierEnabled]);

	useEffect(() => {
		setMagnifierSize(reflectionStore.magnifierSize);
	}, [reflectionStore.magnifierSize]);

	useEffect(() => {
		if (magnifierEnabled && brushConfigs.lineWidth !== getAdjustedBrushSize()) {
			brushConfigs.lineWidth = getAdjustedBrushSize();
		} else {
			brushConfigs.lineWidth = 50;
		}
	}, [magnifierEnabled]);

	const drawSingleLayer = async (layer: AutomotiveReflectionLayer | AutomotiveImageSegmentation) => {
		if (!width || !height || !layer.url) return;

		const canvas = layerCanvasRefs.current.get(layer.name);
		if (!canvas) return;

		if (canvas.getAttribute("data-url") === layer.url) {
			canvas.style.opacity = `${(layer as AutomotiveReflectionLayer).intensity}`;
			return;
		}
		reflectionStore.selectedSegmentationRef = layerCanvasRefs;

		canvas.width = width;
		canvas.height = height;
		const ctx = canvas.getContext("2d");
		if (!ctx) return;

		ctx.clearRect(0, 0, width, height);

		try {
			await new Promise<void>((resolve, reject) => {
				const layerUrl = layer.url as string;
				const img = new Image();
				img.crossOrigin = "anonymous";
				img.src = layerUrl;
				img.onload = () => {
					if (layer === selectedSegmentation) {
						const offscreenCanvas = document.createElement("canvas");
						offscreenCanvas.width = width;
						offscreenCanvas.height = height;
						const offscreenCtx = offscreenCanvas.getContext("2d");

						if (offscreenCtx) {
							offscreenCtx.drawImage(img, 0, 0, width, height);

							const imageData = offscreenCtx.getImageData(0, 0, width, height);
							const data = imageData.data;

							for (let i = 0; i < data.length; i += 4) {
								const r = data[i];
								const g = data[i + 1];
								const b = data[i + 2];

								if (r < 30 && g < 30 && b < 30) {
									data[i + 3] = 0; // Set alpha to 0
								} else if (r > 200 && g > 200 && b > 200) {
									data[i] = 117;
									data[i + 1] = 51;
									data[i + 2] = 211;
									data[i + 3] = 150;
								}
							}

							ctx.putImageData(imageData, 0, 0);
						} else {
							ctx.globalAlpha = 1;
							ctx.drawImage(img, 0, 0, width, height);
						}
					} else {
						ctx.globalAlpha = 1;
						ctx.drawImage(img, 0, 0, width, height);
					}

					canvas.setAttribute("data-url", layerUrl);
					resolve();
				};
				img.onerror = reject;

				setTimeout(resolve, 5000);
			});
		} catch (error) {
			console.error(`Failed to load image for layer ${layer.name}:`, error);
		}
	};

	const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
		if (magnifierEnabled && localCanvasRef.current) {
			const rect = localCanvasRef.current.getBoundingClientRect();
			const x = e.clientX - rect.left;
			const y = e.clientY - rect.top;

			const boundedX = Math.max(0, Math.min(rect.width, x));
			const boundedY = Math.max(0, Math.min(rect.height, y));

			setMagnifierPosition({ x: boundedX, y: boundedY });
		}
	};

	const renderMagnifier = () => {
		if (!magnifierEnabled || !localCanvasRef.current) return null;

		const zoom = reflectionStore.magnifierZoom;
		const halfSize = magnifierSize / 2;

		const canvasRect = localCanvasRef.current.getBoundingClientRect();
		const canvasWidth = canvasRect.width;
		const canvasHeight = canvasRect.height;

		return (
			<Box
				className={styles.magnifier}
				style={{
					position: "absolute",
					left: magnifierPosition.x - halfSize,
					top: magnifierPosition.y - halfSize,
					width: magnifierSize,
					height: magnifierSize,
					borderRadius: "50%",
					border: "2px solid #333",
					overflow: "hidden",
					pointerEvents: "none",
					zIndex: 2,
					boxShadow: "0 0 10px rgba(0,0,0,0.3)",
				}}
			>
				<Box
					style={{
						position: "absolute",
						width: canvasWidth * zoom,
						height: canvasHeight * zoom,
						transform: `translate(${-magnifierPosition.x * zoom + halfSize}px, ${
							-magnifierPosition.y * zoom + halfSize
						}px)`,
					}}
				>
					<canvas
						width={width}
						height={height}
						style={{
							position: "absolute",
							left: 0,
							top: 0,
							width: "100%",
							height: "100%",
						}}
						ref={(el) => {
							if (el && localCanvasRef.current) {
								const ctx = el.getContext("2d");
								if (ctx) {
									ctx.clearRect(0, 0, el.width, el.height);
									ctx.drawImage(localCanvasRef.current, 0, 0, el.width, el.height);
								}
							}
						}}
					/>

					<canvas
						width={width}
						height={height}
						style={{
							position: "absolute",
							left: 0,
							top: 0,
							width: "100%",
							height: "100%",
							opacity: 0.5,
						}}
						ref={(el) => {
							if (el && localCanvasOverlayRef.current) {
								const ctx = el.getContext("2d");
								if (ctx) {
									ctx.clearRect(0, 0, el.width, el.height);
									ctx.drawImage(localCanvasOverlayRef.current, 0, 0, el.width, el.height);
								}
							}
						}}
					/>

					{selectedSegmentation && (
						<canvas
							width={width}
							height={height}
							style={{
								position: "absolute",
								left: 0,
								top: 0,
								width: "100%",
								height: "100%",
							}}
							ref={(el) => {
								if (el && layerCanvasRefs.current.get(selectedSegmentation.name)) {
									const ctx = el.getContext("2d");
									if (ctx) {
										ctx.clearRect(0, 0, el.width, el.height);
										ctx.drawImage(
											layerCanvasRefs.current.get(selectedSegmentation.name)!,
											0,
											0,
											el.width,
											el.height,
										);
									}
								}
							}}
						/>
					)}
				</Box>
				<Box
					className={styles.magnifierCursor}
					style={{
						background: brushConfigs.strokeStyle,
						opacity: brushConfigs.brushColorOpacity,
					}}
					id="magnifierBrush"
				/>
			</Box>
		);
	};

	const getAdjustedBrushSize = () => {
		return magnifierEnabled ? brushConfigs.lineWidth / reflectionStore.magnifierZoom : brushConfigs.lineWidth;
	};

	useEffect(() => {
		const cursorElement = cursorRef.current;
		const magnifierBrush = document.getElementById("magnifierBrush");
		if (cursorElement) {
			cursorElement.style.width = `${getAdjustedBrushSize()}px`;
			cursorElement.style.height = `${getAdjustedBrushSize()}px`;
		}
		if (magnifierBrush) {
			magnifierBrush.style.width = `${getAdjustedBrushSize()}px`;
			magnifierBrush.style.height = `${getAdjustedBrushSize()}px`;
			(magnifierBrush.style as any).zoom = `${5 * reflectionStore.magnifierZoom - 5.5}`;
		}
	}, [brushConfigs.lineWidth, magnifierEnabled, reflectionStore.magnifierZoom]);

	return (
		<Box className={clsx(styles.canvasContainer, "automotive-canvas-container")} ref={containerRef}>
			{selectedSegmentation && !magnifierEnabled && (
				<Box
					ref={cursorRef}
					className={styles.customCursor}
					style={{
						width: getAdjustedBrushSize(),
						height: getAdjustedBrushSize(),
						backgroundColor: brushConfigs.strokeStyle,
						opacity: brushConfigs.brushColorOpacity,
					}}
				></Box>
			)}

			<canvas
				onContextMenu={(e) => e.preventDefault()}
				className={styles.briaCanvas}
				ref={localCanvasRef}
				style={{ pointerEvents: selectedSegmentation ? "auto" : "none" }}
				onMouseMove={handleMouseMove}
			/>
			<canvas onContextMenu={(e) => e.preventDefault()} ref={localCanvasOverlayRef} />

			<img src={baseImageUrl || "/placeholder.svg"} className={styles.baseCanvas} />
			{selectedSegmentation && (
				<canvas
					key={`${selectedSegmentation.name}_segmentation`}
					ref={(el) => {
						if (el) {
							layerCanvasRefs.current.set(selectedSegmentation.name, el);
							if (width && height && selectedSegmentation.url) {
								drawSingleLayer(selectedSegmentation);
							}
						}
					}}
					className={styles.layerCanvas}
				/>
			)}
			{!selectedSegmentation &&
				reflectionLayers
					?.filter((layer) => !layer.hidden)
					.map((layer) => (
						<canvas
							key={layer.name}
							ref={(el) => {
								if (el) {
									layerCanvasRefs.current.set(layer.name, el);
									if (width && height && layer.url) {
										drawSingleLayer(layer);
									}
								}
							}}
							className={styles.layerCanvas}
							style={{ opacity: layer.intensity }}
						/>
					))}
			{magnifierEnabled && renderMagnifier()}
		</Box>
	);
};

export default observer(AutomotiveCanvas);
